import React, { useEffect, useMemo, useState } from "react";
import { createSearchParams, useNavigate } from "react-router-dom";
import {
  BellContainer,
  ContentContainer,
  ContentBell,
  RightFunctions,
  NotificationItem,
  NotificationItemContainer,
  NotificationFooter,
  VisualSignaling,
  EmptyStateContainer
} from "./Notifications.styled";
import Tabs2 from "../Tabs2/Tabs2";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { observer } from "mobx-react";
import { useStore } from "../../../hooks";
import { NameInitialsAvatar } from "react-name-initials-avatar";
import { ItemStatus, NotificationTab } from "../../../utils/enums";
import { Icon } from "../../icon";
import timeAgo from "../../../utils/timeAgo";

const SYSTEM_NOTIFICACION = "annaims_notification";

const tabs = [
  {
    label: "Unread",
    permission: null,
  },
  {
    label: "Read",
    permission: null,
  },
];

const Notifications = observer((props) => {
  const { notificationStore, authStore } = useStore();
  const [currentTab, setCurrentTab] = useState(NotificationTab.UnreadTab);
  const navigate = useNavigate();

  const { setIsActive } = props;

  const users = useMemo(() => authStore.users, [authStore.users]);

  const onToggleTab = (currentTab) => {
    setCurrentTab(currentTab);
  };

  const handleViewAllNotifications = () => {
    navigate("v2/notifications/");
  };

  const redirectByModuleType = async (notification) => {
    const { metadata } = notification || {};
    const { module_type, id } = metadata || {};

    if (notification.status === ItemStatus.unread) {
      await notificationStore.setItemStatusById(
        [notification.id],
        ItemStatus.read,
      );
      await notificationStore.putReadNotification({
        user_id: notification.user,
        notification_id: [notification.id],
        customer_id: authStore.userData.customer,
      });
    }

    if (!module_type) return false;

    const navigateTo = (pathname, id) => {
      navigate({
        pathname,
        search: id ? createSearchParams({ id }).toString() : undefined,
      });
    };

    const routes = {
      conversation: () => navigateTo("/v2/conversations/marketing", id),
      contact: () =>
        navigateTo(id ? "/unified-profile" : "/v2/conversations", id),
    };

    const routeHandler = routes[module_type];
    if (routeHandler) routeHandler();

    setIsActive(false);
  };

  const getItemsByStatus = (status) => {
    const notifications = notificationStore.getItemsBellByStatus(status);

    if (!notifications || notifications.length === 0) {
      return (
        <EmptyStateContainer>
          <Icon name="bellGrayNotification" width="20px" height="20px" />
          <div className="message">No New Notifications</div>
          <div className="subMessage">You're all caught up!</div>
        </EmptyStateContainer>
      );
    }
    

    return notifications.map((notification) => {
      const user = users?.find((user) => user.id === notification.user);

        const getFullName = () => {
          const hasFirstname = user?.first_name;
          const hasLastname = user?.last_name;

          if (hasFirstname && hasLastname)
            return `${user?.first_name} ${user?.last_name}`;

          return SYSTEM_NOTIFICACION;
        };

        return (
          <Notifications.Item
            key={notification.id}
            onClick={() => redirectByModuleType(notification)}
            isUnread={notification.status === ItemStatus.unread}
            name={getFullName()}
            description={notification.description}
            time={notification.time}
          />
        );
      });
  };

  const onMarkAllAsRead = async () => {
    const userData = { ...authStore?.userData };
    const status = ItemStatus.unread;
    const allUnreadIds = notificationStore
      .getItemsBellByStatus(status)
      ?.map((notification) => notification.id);

    await notificationStore.setItemStatusById(allUnreadIds, ItemStatus.read);
    await notificationStore.putReadNotification({
      user_id: "",
      notification_id: allUnreadIds,
      customer_id: userData?.customer,
    });
  };

  return (
    <ContentContainer>
      <ContentBell>
        <Tabs2
          defaultTab={NotificationTab.UnreadTab}
          tabs={tabs}
          onToggleTab={onToggleTab}
        />
        {currentTab === NotificationTab.UnreadTab && (
          <RightFunctions>
            <span onClick={onMarkAllAsRead}>Mark all as read</span>
            <OpenInNewIcon htmlColor="#545454" />
          </RightFunctions>
        )}
      </ContentBell>
      <div>
        <Tabs2.Outlet show={currentTab === NotificationTab.UnreadTab}>
          <NotificationItemContainer>
            {getItemsByStatus(ItemStatus.unread)}
          </NotificationItemContainer>
        </Tabs2.Outlet>

        <Tabs2.Outlet show={currentTab === NotificationTab.ReadTab}>
          <NotificationItemContainer>
            {getItemsByStatus(ItemStatus.read)}
          </NotificationItemContainer>
        </Tabs2.Outlet>
      </div>
      <NotificationFooter onClick={handleViewAllNotifications}>
        View all notifications
      </NotificationFooter>
    </ContentContainer>
  );
});

Notifications.Item = (props) => {
  const { isUnread, name, description, time, onClick } = props;

  const isAnnaims = name === SYSTEM_NOTIFICACION;
  const nameToShow = isAnnaims ? "Annaims Notification" : name;
  return (
    <NotificationItem
      isUnread={isUnread}
      isAnnaims={isAnnaims}
      onClick={onClick}
    >
      <div className="photo">
        {isAnnaims ? (
          <Icon name="iconColored" height={"32px"} width={"32px"} />
        ) : (
          <NameInitialsAvatar
            name={nameToShow}
            size="38px"
            bgColor={isAnnaims ? "#6d6d6d" : "#2173FF"}
            textColor="white"
            borderWidth="0"
          />
        )}
      </div>
      <div className="description">
        <div className="name">{nameToShow}</div>
        <div className="eventDescription">{description}</div>
        <div className="timeAgo">{timeAgo(time)}</div>
      </div>
    </NotificationItem>
  );
};

const Bell = observer((props) => {
  const { notificationStore } = useStore();
  const [showVisualSignaling, setShowVisualSignaling] = useState(false);
  const { isActive, setIsActive } = props;
  const totalUnreadedNotification = notificationStore.getItemsBellByStatus(
    ItemStatus.unread,
  ).length;
  const hasNotificationsUnreaded = totalUnreadedNotification;

  useEffect(() => {
    setShowVisualSignaling(hasNotificationsUnreaded);
  }, [hasNotificationsUnreaded]);

  return (
    <BellContainer isActive={isActive} onClick={() => setIsActive(!isActive)}>
      {showVisualSignaling ? (
        <VisualSignaling>
          <span>
            {totalUnreadedNotification === 1
              ? "1"
              : totalUnreadedNotification >= 10
                ? "9+"
                : `${totalUnreadedNotification}+`
            }
          </span>
        </VisualSignaling>
      ) : (
        ""
      )}
      <Icon name="bellNotification" width="20px" height="20px" />
    </BellContainer>
  );
});

Notifications.Bell = Bell;

export default Notifications;
